import React, { useRef, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical } from 'lucide-react';

export const DASHBOARD_PANEL_GRID_CLASS =
    'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 auto-rows-[120px] grid-flow-dense';

const GRID_ROW_PX = 120;
const GRID_GAP_PX = 16;
const WIDE_WIDGETS = new Set(['teamStatus', 'pendingApprovals', 'myLeaveSummary', 'leaveEntitlements']);

const COL_CLASS = {
    1: 'col-span-1',
    2: 'col-span-1 md:col-span-2 xl:col-span-2',
    3: 'col-span-1 md:col-span-2 xl:col-span-3',
    4: 'col-span-full'
};

const ROW_CLASS = {
    1: 'row-span-1',
    2: 'row-span-2',
    3: 'row-span-3',
    4: 'row-span-4',
    5: 'row-span-5',
    6: 'row-span-6',
    7: 'row-span-7',
    8: 'row-span-8'
};

export function getDefaultPanelSize(key) {
    if (key === 'deviceReachability') return { cols: 4, rows: 1 };
    if (key === 'qualitySummary') return { cols: 4, rows: 2 };
    if (WIDE_WIDGETS.has(key)) return { cols: 2, rows: 3 };
    return { cols: 1, rows: 2 };
}

export function getPanelSizeLimits(key) {
    if (key === 'deviceReachability') return { minCols: 1, maxCols: 4, minRows: 1, maxRows: 4 };
    if (key === 'qualitySummary') return { minCols: 2, maxCols: 4, minRows: 2, maxRows: 6 };
    if (WIDE_WIDGETS.has(key)) return { minCols: 1, maxCols: 4, minRows: 2, maxRows: 8 };
    return { minCols: 1, maxCols: 4, minRows: 1, maxRows: 6 };
}

export function panelSizeToClass(size) {
    const cols = Math.min(4, Math.max(1, size?.cols || 1));
    const rows = Math.min(8, Math.max(1, size?.rows || 1));
    return `${COL_CLASS[cols]} ${ROW_CLASS[rows]}`;
}

function clampSize(key, cols, rows) {
    const limit = getPanelSizeLimits(key);
    return {
        cols: Math.min(limit.maxCols, Math.max(limit.minCols, cols)),
        rows: Math.min(limit.maxRows, Math.max(limit.minRows, rows))
    };
}

function gridColumnCount(width) {
    if (width >= 1280) return 4;
    if (width >= 768) return 2;
    return 1;
}

function SortableCard({ id, size, spanClass, onRemove, onResize, gridRef, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const handleResizePointerDown = (event) => {
        event.stopPropagation();
        event.preventDefault();
        const grid = gridRef?.current;
        if (!grid || !onResize) return;

        const colCount = gridColumnCount(grid.clientWidth);
        const cellW = (grid.clientWidth - GRID_GAP_PX * (colCount - 1)) / colCount;
        const startX = event.clientX;
        const startY = event.clientY;
        const startCols = size?.cols || 1;
        const startRows = size?.rows || 1;
        const pointerId = event.pointerId;
        event.currentTarget.setPointerCapture(pointerId);

        const onMove = (ev) => {
            const nextCols = startCols + Math.round((ev.clientX - startX) / (cellW + GRID_GAP_PX));
            const nextRows = startRows + Math.round((ev.clientY - startY) / (GRID_ROW_PX + GRID_GAP_PX));
            onResize(id, clampSize(id, nextCols, nextRows));
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Translate.toString(transform),
                transition,
                zIndex: isDragging ? 80 : 1
            }}
            className={`min-w-0 min-h-0 h-full ${spanClass || ''} ${isDragging ? 'opacity-30' : ''}`}
        >
            <div
                className="relative h-full cursor-grab active:cursor-grabbing touch-none rounded-2xl ring-2 ring-indigo-500/60 dark:ring-indigo-400/60 shadow-lg"
                {...attributes}
                {...listeners}
            >
                {/* Sol üst: Sürükleme & Boyut Göstergesi */}
                <div className="absolute top-2 left-2 z-20 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900/80 text-white text-[10px] font-bold backdrop-blur-sm pointer-events-none shadow">
                    <GripVertical size={11} className="text-indigo-300" />
                    <span>{size?.cols || 1}×{size?.rows || 1}</span>
                </div>

                {/* Sağ üst: Paneli Kaldır */}
                <button
                    type="button"
                    onClick={onRemove}
                    onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                    className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-red-600 text-white hover:bg-red-500 shadow-md pointer-events-auto transition-transform hover:scale-110"
                    title="Paneli kaldır"
                >
                    <X size={14} />
                </button>

                {/* Sağ alt: Boyutlandırma Tutamacı (Hem açık hem koyu temada belirgin) */}
                <div
                    role="separator"
                    aria-label="Boyutu değiştir"
                    title="Köşeden tutup büyüt / küçült"
                    onPointerDown={handleResizePointerDown}
                    className="absolute bottom-0 right-0 z-20 h-7 w-7 cursor-se-resize pointer-events-auto touch-none flex items-end justify-end p-1.5 bg-indigo-600/90 hover:bg-indigo-500 text-white rounded-tl-xl rounded-br-2xl shadow"
                >
                    <span className="h-2.5 w-2.5 border-r-2 border-b-2 border-white rounded-br-[2px]" />
                </div>

                <div className="h-full min-h-0 pointer-events-none [&>*]:h-full [&>*]:min-h-0 [&>*]:overflow-hidden">
                    {children}
                </div>
            </div>
        </div>
    );
}

export default function DashboardSortableGrid({ items, onReorder, onRemove, onResize, renderItem }) {
    const [activeId, setActiveId] = useState(null);
    const gridRef = useRef(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    const activeItem = items.find((x) => x.key === activeId);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over || active.id === over.id) return;
        const oldIndex = items.findIndex((x) => x.key === active.id);
        const newIndex = items.findIndex((x) => x.key === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onReorder(arrayMove(items.map((x) => x.key), oldIndex, newIndex));
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={({ active }) => setActiveId(String(active.id))}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={items.map((x) => x.key)} strategy={rectSortingStrategy}>
                <div ref={gridRef} className={DASHBOARD_PANEL_GRID_CLASS}>
                    {items.map((item) => (
                        <SortableCard
                            key={item.key}
                            id={item.key}
                            size={item.size}
                            spanClass={item.spanClass}
                            onRemove={() => onRemove(item.key)}
                            onResize={onResize}
                            gridRef={gridRef}
                        >
                            {renderItem(item.key)}
                        </SortableCard>
                    ))}
                </div>
            </SortableContext>
            <DragOverlay>
                {activeItem ? (
                    <div className="pointer-events-none opacity-90 shadow-2xl rounded-2xl ring-2 ring-indigo-400 max-w-md">
                        {renderItem(activeItem.key)}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
